import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  Download,
  FileJson,
  FileText,
  Loader2,
  Search,
  WifiOff,
  Bell,
  Dna,
  Trash2,
  Lock,
  ArrowRight,
  Users,
  BookOpen,
  Settings2,
  Shield,
  Flame,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ArchivePrivacyLock } from './ArchivePrivacyLock';
import { ArchiveGeneralSettings } from './ArchiveGeneralSettings';
import { ArchivePersonalizationSettings } from './ArchivePersonalizationSettings';
import { ArchiveSettingsMemorialSection } from './ArchiveSettingsMemorialSection';
import { DocumentVault } from '../vault/DocumentVault';
import { treeHref, personHref } from '../../utils/prettyArchiveLinks';
import { memorialManageHref, memorialsHref } from '../../utils/memorial';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { isGenealogicalPerson } from '../../utils/companionProfiles';
import { AiLegacyBookExportPanel } from '../export/AiLegacyBookExportPanel';
import { TrustProofGrid } from '../trust/TrustProofGrid';
import { FIRST_CHAPTER_EXPORT } from '../../content/publicSiteContent';
import { ContextualGuideTip } from '../guide/ContextualGuideTip';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAIN_SECTIONS = [
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'privacy', label: 'Privacy', icon: Shield },
  { id: 'people', label: 'People & connections', icon: Users },
  { id: 'stories', label: 'Stories & documents', icon: BookOpen },
  { id: 'reminders', label: 'Reminders', icon: Bell },
  { id: 'exports', label: 'Exports', icon: Download },
  { id: 'memorials', label: 'Memorials', icon: Flame },
  { id: 'dna', label: 'DNA', icon: Dna },
];

const DANGER_SECTION = { id: 'danger', label: 'Danger zone', icon: AlertTriangle };

function StatusChip({ children, tone = 'muted' }) {
  const tones = {
    muted: 'border-border/50 bg-muted/30 text-muted-foreground',
    active: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700',
    accent: 'border-[#C9A961]/30 bg-[#C9A961]/10 text-[#6b5f42]',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
        tones[tone] || tones.muted
      )}
    >
      {children}
    </span>
  );
}

function SectionHeader({ title, description, action }) {
  return (
    <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h2 className="font-serif text-lg font-medium text-foreground">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </header>
  );
}

function PolicyRow({ label, hint, badge }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground/90">{label}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {badge ? (
        <span className="shrink-0 self-start rounded-full border border-border/45 bg-background px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:self-center">
          {badge}
        </span>
      ) : null}
    </div>
  );
}

function PlannedRow({ title, hint }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg bg-muted/10 px-4 py-3">
      <div>
        <p className="text-sm text-foreground/85">{title}</p>
        {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
        Planned
      </span>
    </div>
  );
}

function ArchiveSettingsHeader({ tree, peopleCount, visibility, canExportArchive, isOnline }) {
  const coverUrl = tree?.cover_image_url
    ? resolveAssetUrl(BACKEND_URL, tree.cover_image_url)
    : null;
  const isPrivate = visibility !== 'discoverable';

  return (
    <div
      className="rounded-xl border border-border/45 bg-[hsl(42_28%_99%)]/90 p-4 sm:p-5"
      data-testid="archive-settings-header-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3.5">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt=""
              className="h-14 w-14 shrink-0 rounded-lg border border-border/40 object-cover"
            />
          ) : (
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-border/40 bg-primary/8 text-primary">
              <Archive className="h-6 w-6" aria-hidden />
            </span>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-serif text-xl font-light text-foreground">Archive settings</h1>
              <StatusChip tone="accent">Family archive</StatusChip>
            </div>
            <p className="mt-0.5 truncate font-medium text-foreground/90">{tree?.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage privacy, exports, memorial defaults, and who can collaborate.
            </p>
            <Link
              to="/settings"
              className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Account settings
              <ExternalLink className="h-3 w-3 opacity-60" aria-hidden />
            </Link>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <StatusChip tone={isPrivate ? 'muted' : 'active'}>{isPrivate ? 'Private' : 'Discoverable'}</StatusChip>
          <StatusChip>
            {peopleCount} {peopleCount === 1 ? 'person' : 'people'}
          </StatusChip>
          {canExportArchive && isOnline && peopleCount > 0 ? (
            <StatusChip tone="active">Ready to export</StatusChip>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ArchiveSettingsPanel({
  tree,
  user,
  people = [],
  canEditTree,
  isOnline,
  peopleCount = 0,
  visibility,
  visibilityLoading,
  indexedSurnames = 0,
  onVisibilityToggle,
  onTreeUpdated,
  onExportPdf,
  onExportJson,
  exportingPdf,
  exportingJson,
  canExportArchive,
  onDeleteArchive,
  deletingArchive,
}) {
  const [activeSection, setActiveSection] = useState('general');
  const isOwner = tree?.role === 'owner';

  const genealogicalPeople = useMemo(
    () => (people || []).filter(isGenealogicalPerson),
    [people]
  );
  const firstMemorialPerson = genealogicalPeople[0];

  const allSections = [...MAIN_SECTIONS, DANGER_SECTION];
  const current = allSections.find((s) => s.id === activeSection) || MAIN_SECTIONS[0];

  const navButton = (section, { danger = false } = {}) => {
    const Icon = section.icon;
    const active = activeSection === section.id;
    return (
      <button
        key={section.id}
        type="button"
        onClick={() => setActiveSection(section.id)}
        className={cn(
          'flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-left text-[13px] transition-colors',
          active
            ? 'bg-[hsl(38_22%_92%)]/95 font-medium text-foreground shadow-sm ring-1 ring-border/30'
            : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
          danger && !active && 'text-destructive/75 hover:text-destructive'
        )}
        data-testid={`archive-settings-nav-${section.id}`}
      >
        <Icon className="h-4 w-4 shrink-0 opacity-75" aria-hidden />
        <span className="truncate">{section.label}</span>
      </button>
    );
  };

  const renderPanel = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <SectionHeader title="General" description="Name, description and cover." />
            <ArchiveGeneralSettings
              tree={tree}
              user={user}
              canEdit={canEditTree}
              onTreeUpdated={onTreeUpdated}
            />
          </>
        );

      case 'privacy':
        return (
          <>
            <SectionHeader title="Privacy" description="Private by default — you choose what is shared." />
            {tree ? (
              <div className="space-y-4">
                <ArchivePrivacyLock treeId={tree.id} isOwner={isOwner} />
                {isOwner ? (
                  <Card className="border-border/40 p-4 sm:p-5" data-testid="discovery-settings-card">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0 space-y-2">
                        <h4 className="text-sm font-medium">Surname cluster discovery</h4>
                        <p className="text-xs text-muted-foreground">
                          Opt in so researchers may find shared surname connections.
                        </p>
                        <StatusChip tone={visibility === 'discoverable' ? 'active' : 'muted'}>
                          {visibility === 'discoverable'
                            ? `On · ${indexedSurnames} surname${indexedSurnames !== 1 ? 's' : ''}`
                            : 'Off'}
                        </StatusChip>
                      </div>
                      <Button
                        variant={visibility === 'discoverable' ? 'outline' : 'default'}
                        className="rounded-full shrink-0"
                        onClick={() =>
                          onVisibilityToggle?.(visibility === 'discoverable' ? 'private' : 'discoverable')
                        }
                        disabled={visibilityLoading || peopleCount === 0}
                        data-testid="visibility-toggle-btn"
                      >
                        {visibilityLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving…
                          </>
                        ) : visibility === 'discoverable' ? (
                          'Turn off discovery'
                        ) : (
                          'Enable discovery'
                        )}
                      </Button>
                    </div>
                    <PlannedRow title="DNA matching visibility" hint="Show DNA overlap in discovery." />
                  </Card>
                ) : null}
                <TrustProofGrid variant="compact" showHeader={false} className="pt-2" />
              </div>
            ) : null}
          </>
        );

      case 'people':
        return (
          <>
            <SectionHeader
              title="People & connections"
              description="Manage how people are added, verified and invited."
              action={
                <Button variant="default" size="sm" className="rounded-full" asChild>
                  <Link to={`${treeHref(tree)}/members`}>
                    Manage members
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </Button>
              }
            />
            <div className="space-y-2">
              <PolicyRow
                label="New people default to private"
                hint="Added people stay private until verified or claimed."
                badge="Private default"
              />
              <PlannedRow title="Verification before verified badge" />
              <PlannedRow title="Suggest connections" hint="Collaborators propose links for review." />
            </div>
          </>
        );

      case 'stories':
        return (
          <>
            <SectionHeader
              title="Stories & documents"
              description="Upload defaults and your document vault."
            />
            <div className="space-y-4">
              <div className="space-y-2">
                <PolicyRow label="Story visibility" hint="New stories stay private." badge="Private default" />
                <PolicyRow label="Contributors" hint="Set via member invites." badge="Via invites" />
                <PolicyRow label="Upload default" hint="Files start in your private archive." badge="Private default" />
              </div>
              {tree ? (
                <>
                  <ArchivePersonalizationSettings className="rounded-lg border border-border/35 bg-muted/10 p-4" />
                  <div id="archive-document-vault">
                    <DocumentVault treeId={tree.id} canEdit={canEditTree} />
                  </div>
                </>
              ) : null}
            </div>
          </>
        );

      case 'reminders':
        return (
          <>
            <SectionHeader
              title="Reminders"
              description="Birthdays, remembrance days and family dates."
              action={
                <Button size="sm" className="rounded-full" asChild>
                  <Link to="/reminders">
                    Open reminders
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </Button>
              }
            />
            <div className="space-y-2">
              <PolicyRow label="Birthday & remembrance reminders" badge="Active" />
              <PlannedRow title="Weekly storybook prompts" />
              <PlannedRow title="Email reminders" hint="Requires email delivery setup." />
            </div>
          </>
        );

      case 'exports':
        return (
          <>
            <SectionHeader
              title="Exports"
              description="Download a keepsake legacy book or a review draft with readiness notes."
              action={
                <Button
                  size="sm"
                  className="rounded-full"
                  onClick={() => onExportPdf?.('keepsake')}
                  disabled={exportingPdf || peopleCount === 0 || !isOnline || !canExportArchive}
                  data-testid="export-pdf-btn-header"
                >
                  {exportingPdf ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Keepsake PDF
                </Button>
              }
            />
            {!isOnline ? (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <WifiOff className="h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">Offline — connect to download exports.</p>
              </div>
            ) : null}
            <AiLegacyBookExportPanel
              treeId={tree?.id}
              canExport={canExportArchive}
              peopleCount={peopleCount}
              onExportPdf={onExportPdf}
              exportingPdf={exportingPdf}
            />
            <div className="rounded-xl border border-[#c9a961]/25 bg-[#c9a961]/5 px-4 py-4">
              <p className="text-sm font-medium text-foreground/90">{FIRST_CHAPTER_EXPORT.title}</p>
              <p className="mt-1 text-xs font-medium text-[#8b7355]">{FIRST_CHAPTER_EXPORT.tagline}</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{FIRST_CHAPTER_EXPORT.description}</p>
            </div>
            <div className="space-y-3">
              <div className="flex flex-col gap-3 rounded-lg border border-border/40 bg-muted/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-3 min-w-0">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-[#C16644]" />
                  <div>
                    <p className="text-sm font-medium">Legacy Book — Keepsake edition</p>
                    <p className="text-xs text-muted-foreground">
                      Finished family book with completed stories, profiles, documents and archive links.
                    </p>
                  </div>
                </div>
                <Button
                  className="rounded-full shrink-0"
                  onClick={() => onExportPdf?.('keepsake')}
                  disabled={exportingPdf || peopleCount === 0 || !isOnline || !canExportArchive}
                  data-testid="export-pdf-btn"
                >
                  {exportingPdf ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Download keepsake
                    </>
                  )}
                </Button>
              </div>
              <div className="flex flex-col gap-3 rounded-lg border border-border/40 bg-muted/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-3 min-w-0">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Legacy Book — Review draft</p>
                    <p className="text-xs text-muted-foreground">
                      Includes unanswered prompts and readiness suggestions — not for sharing.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="rounded-full shrink-0"
                  onClick={() => onExportPdf?.('review')}
                  disabled={exportingPdf || peopleCount === 0 || !isOnline || !canExportArchive}
                  data-testid="export-pdf-review-btn"
                >
                  Review draft PDF
                </Button>
              </div>
              <div className="flex flex-col gap-3 rounded-lg border border-border/40 bg-muted/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-3 min-w-0">
                  <FileJson className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium">Full backup (JSON)</p>
                    <p className="text-xs text-muted-foreground">Complete archive data export.</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="rounded-full shrink-0"
                  onClick={onExportJson}
                  disabled={exportingJson || peopleCount === 0 || !isOnline || !canExportArchive}
                  data-testid="export-json-btn"
                >
                  {exportingJson ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Download JSON
                    </>
                  )}
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" asChild>
                <Link to="/storybook">
                  <BookOpen className="mr-1.5 h-3.5 w-3.5" />
                  Open Storybook Mode
                </Link>
              </Button>
              {peopleCount === 0 ? (
                <p className="text-sm text-muted-foreground">Add people before exporting.</p>
              ) : null}
              <TrustProofGrid variant="compact" showHeader={false} className="pt-2" />
            </div>
          </>
        );

      case 'memorials':
        return (
          <>
            <SectionHeader
              title="Memorials"
              description="Optional, private by default."
              action={
                firstMemorialPerson ? (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" asChild>
                      <Link to={memorialsHref(tree?.id)}>
                        All memorials
                      </Link>
                    </Button>
                    <Button size="sm" className="rounded-full" asChild>
                      <Link to={memorialManageHref(firstMemorialPerson)}>
                        Open memorial workspace
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" asChild>
                      <Link to={memorialsHref(tree?.id)}>View memorials</Link>
                    </Button>
                  </div>
                )
              }
            />
            <ArchiveSettingsMemorialSection people={people} treeId={tree?.id} />
          </>
        );

      case 'dna':
        return (
          <>
            <SectionHeader
              title="DNA"
              description="Genealogy storage only — no health interpretation."
              action={
                <Button size="sm" className="rounded-full" asChild>
                  <Link to="/dna">
                    Open DNA management
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </Button>
              }
            />
            <div className="space-y-3">
              <StatusChip tone="accent">Beta · Testing</StatusChip>
              <PolicyRow label="Storage & matching consent" hint="Manage on the DNA page." badge="On DNA page" />
              <p className="text-xs text-muted-foreground">No health, ethnicity or medical reports.</p>
            </div>
          </>
        );

      case 'danger':
        return (
          <>
            <SectionHeader title="Danger zone" description="Sensitive, irreversible actions." />
            <div className="space-y-3 rounded-lg border border-destructive/20 bg-destructive/[0.04] p-4">
              <PlannedRow title="Transfer ownership" hint="Move archive to another account." />
              {isOwner ? (
                <div className="flex flex-col gap-3 rounded-lg border border-destructive/25 bg-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-destructive">Delete archive</p>
                    <p className="text-xs text-muted-foreground">Soft-deletes people, stories and documents.</p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="rounded-full shrink-0"
                    disabled={deletingArchive}
                    onClick={onDeleteArchive}
                    data-testid="archive-delete-btn"
                  >
                    {deletingArchive ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Delete archive
                  </Button>
                </div>
              ) : (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" />
                  Only the owner can delete this archive.
                </p>
              )}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="mx-auto w-full max-w-7xl px-4 pb-8 pt-4 sm:px-6 sm:pt-5 lg:px-8 lg:pt-6"
      data-testid="archive-settings-panel"
    >
      <ArchiveSettingsHeader
        tree={tree}
        peopleCount={peopleCount}
        visibility={visibility}
        canExportArchive={canExportArchive}
        isOnline={isOnline}
      />

      <ContextualGuideTip tipId="archive-settings" title="Archive settings" className="mt-4">
        Privacy, exports, reminders, memorial defaults and archive identity.
      </ContextualGuideTip>

      <div className="mb-4 mt-4 lg:hidden">
        <Select value={activeSection} onValueChange={setActiveSection}>
          <SelectTrigger className="rounded-lg" aria-label="Settings section">
            <SelectValue>{current.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {allSections.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        <nav className="hidden lg:block" aria-label="Archive settings sections">
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
            Archive controls
          </p>
          <div className="space-y-0.5 lg:sticky lg:top-4">
            {MAIN_SECTIONS.map((s) => navButton(s))}
            <div className="my-3 border-t border-border/40" />
            {navButton(DANGER_SECTION, { danger: true })}
          </div>
        </nav>

        <main
          className="min-w-0 rounded-xl border border-border/40 bg-card/30 p-4 sm:p-5 lg:p-6"
          data-testid={`archive-settings-${activeSection}`}
        >
          {renderPanel()}
        </main>
      </div>
    </div>
  );
}

export default ArchiveSettingsPanel;
